// @flow

import React from 'react';
import styled from '@emotion/styled';

import PlayerSwapConfirmImage from './images/playerSwapConfirm.png';
import PlayerSwapLocation from './images/playerSwapLocation.png';
import DualPosition from './images/dualPosition.png';
import ComparisonImage from './images/comparison.png';
import ErrorImage from './images/error.png';
import NotificationImage from './images/notification.png';

type Props = {
  siteName: string
};

const Image = styled.img(({ maxWidth }) => ({
  maxWidth,
  width: '100%',
  height: 'auto'
}));

const PlayerSwapRules: React.FC<Props> = ({ siteName }: Props) => (
  <div>
    <h2>WHERE ON THE SITE CAN I USE THE PLAYER SWAP?</h2>
    <p>The Swap tab is located in the My Teams Section.</p>
    <p>
      <Image src={PlayerSwapLocation} maxWidth={600} alt="" />
    </p>
    <h2>How does the player swap work?</h2>
    <p>
      Players can be swapped up until the scheduled start time of their game. After the scheduled
      start time has passed players will be locked and are ineligible to be swapped. Players swapped
      in and swapped out must have the same position.
    </p>
    <p>
      Please note {siteName} reserves the right to extend the scheduled start time of games. As an
      example, the first game in a slate may have its scheduled start time extended.
    </p>
    <p>
      The salary of the player being swapped in must be equal to or less than the player being
      swapped out. This may result in loss of salary cap to make a swap. The below example shows a
      swap where $1,760 of your salary cap will be lost to make the swap.
    </p>
    <p>
      <Image
        src={PlayerSwapConfirmImage}
        maxWidth={600}
        alt="$1,450 of your salary cap will be lost"
      />
    </p>
    <h2>Can a player swap be reversed?</h2>
    <p>
      Player swaps cannot be reversed. For example, Player A has a salary of $10,000 and is swapped
      for Player B with a salary of $7,000. This swap results in a $3,000 loss of salary cap. If
      Player B was subsequently to be swapped, they could only be replaced with a player with $7,000
      or less in value.
    </p>
    <h2>CAN I SWAP A PLAYER IN MULTIPLE LINEUPS WITH A COMBINATION OF DIFFERENT PLAYERS?</h2>
    <p>
      No. This solution is solely designed to swap individual players, not to restructure a lineup.
    </p>
    <h2>What happens when swapping a player I have in different slates?</h2>
    <p>
      Players can only be swapped for one slate at a time. If you want to swap a player you have in
      multiple slates you will need to make an individual swap for each slate.
    </p>
    <h2>How does a dual-position player swap work?</h2>
    <p>
      If you have selected a dual-position player in each of their available positions they will be
      displayed twice on the player swap out list. Below is an example where Dustin Martin has been
      selected in both the MID and FWD positions. In this situation two individual swaps will be
      required to swap Dustin Martin out (one for each position).
    </p>
    <p>
      <Image src={DualPosition} maxWidth={466} alt="dual-position player swap" />
    </p>
    <h2>
      Can I see the percentage that a player has been selected prior to the scheduled start time of
      their game?
    </h2>
    <p>
      No. Percentages will only be displayed when the player is locked out and can no longer be
      swapped.
    </p>
    <h2>Can I see players not locked in opponent teams?</h2>
    <p>
      No. When comparing teams only players that are locked will be displayed. Below is an example
      of what you will see when looking at an opponent’s team when they have a player not locked.
    </p>
    <p>
      <Image src={ComparisonImage} maxWidth={187} alt="comparing teams" />
    </p>
    <h2>What does exposure % mean?</h2>
    <p>
      This is the percentage of your entries that have a player. This only shows the exposure for
      the slate selected.
    </p>
    <h2>Where on the site can I use the player swap?</h2>
    <p>The Swap tab is located in the My Teams Section.</p>
    <h2>Why didn’t my swap work?</h2>
    <div>
      <p>There are scenarios where a swap will not be allowed. This may include:</p>
      <ul>
        <li>When a player is locked out and is no longer eligible to be swapped.</li>
        <li>
          The swap breaks a contest rule, such as exceeding the maximum players that are allowed in
          a lineup from the one team.
        </li>
        <li>The player you are trying to swap in is already selected in your lineup(s)</li>
      </ul>
      <p>
        If a player selected to swap out is in multiple lineups they will be swapped wherever
        possible. When a swap is unsuccessful the player will need to be swapped again.
      </p>
      <p>Below is an example where only one of two swaps were successful.</p>
      <p>
        <Image src={ErrorImage} maxWidth={512} alt="" />
      </p>
    </div>
    <h2>Can I rely on the player labels?</h2>
    <p>
      Player availability labels are supplied via third-party statistics feeds. We cannot guarantee
      100% accuracy of any third-party feeds.
    </p>
    <h2>Will {siteName} notify me if I have a player with an out label?</h2>
    <p>
      It is always the responsibility of an entrant to monitor their lineups and player status.
      However, {siteName} will automatically check 10-15 mins before the scheduled start time of
      each game in a slate for players with an out label. If you have selected a player with an out
      label a push notification will be sent to you. Note that your mobile phone must be configured
      to accept {siteName} notifications. Below is a sample of the push notification.
    </p>
    <p>
      <Image src={NotificationImage} maxWidth={354} alt="" />
    </p>
    <h2>What if I want to swap a player out but there is no player available to swap in?</h2>
    <p>Unfortunately, you will be unable to make the swap.</p>
    <h2>Will the player swap apply to all contests?</h2>
    <p>Yes, player swap will apply to all sports and contests.</p>
  </div>
);

export default PlayerSwapRules;
